/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CreateInventoryItemRequest } from '../models/CreateInventoryItemRequest';
import type { InventoryItemCountResponse } from '../models/InventoryItemCountResponse';
import type { InventoryItemDto } from '../models/InventoryItemDto';
import type { InventoryItemProjection } from '../models/InventoryItemProjection';
import type { Page_InventoryItemDto_ } from '../models/Page_InventoryItemDto_';
import type { Page_InventoryItemIssueOutHistoryResponse_ } from '../models/Page_InventoryItemIssueOutHistoryResponse_';
import type { Page_InventoryItemStockCountAndValueResponse_ } from '../models/Page_InventoryItemStockCountAndValueResponse_';
import type { Page_InventoryItemStockUpHistoryResponse_ } from '../models/Page_InventoryItemStockUpHistoryResponse_';
import type { UpdateInventoryItemCategoryOnlyRequest } from '../models/UpdateInventoryItemCategoryOnlyRequest';
import type { UpdateInventoryItemRequest } from '../models/UpdateInventoryItemRequest';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class InventoryItemControllerService {
  /**
   * getAllItems
   * @returns Page_InventoryItemDto_ OK
   * @throws ApiError
   */
  public static getAllItemsUsingGet({
    limit = 10,
    offset,
    search,
  }: {
    /**
     * limit
     */
    limit?: number;
    /**
     * offset
     */
    offset?: number;
    /**
     * search
     */
    search?: string;
  }): CancelablePromise<Page_InventoryItemDto_> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/inventory-items',
      query: {
        limit: limit,
        offset: offset,
        search: search,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * createItem
   * @returns InventoryItemDto OK
   * @returns any Created
   * @throws ApiError
   */
  public static createItemUsingPost({
    request,
  }: {
    /**
     * request
     */
    request: CreateInventoryItemRequest;
  }): CancelablePromise<InventoryItemDto | any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/inventory-items',
      body: request,
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * updateItem
   * @returns InventoryItemDto OK
   * @returns any Created
   * @throws ApiError
   */
  public static updateItemUsingPut({
    request,
  }: {
    /**
     * request
     */
    request: UpdateInventoryItemRequest;
  }): CancelablePromise<InventoryItemDto | any> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/inventory-items',
      body: request,
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * updateItemCategory
   * @returns InventoryItemDto OK
   * @returns any Created
   * @throws ApiError
   */
  public static updateItemCategoryUsingPut({
    request,
  }: {
    /**
     * request
     */
    request: UpdateInventoryItemCategoryOnlyRequest;
  }): CancelablePromise<InventoryItemDto | any> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/inventory-items/category',
      body: request,
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * getItemCounts
   * @returns InventoryItemCountResponse OK
   * @throws ApiError
   */
  public static getItemCountsUsingGet(): CancelablePromise<InventoryItemCountResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/inventory-items/counts',
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * getAllItemsStockAndValueDetails
   * @returns Page_InventoryItemStockCountAndValueResponse_ OK
   * @throws ApiError
   */
  public static getAllItemsStockAndValueDetailsUsingGet({
    categoryId,
    limit = 10,
    offset,
    search,
    status,
  }: {
    /**
     * categoryId
     */
    categoryId?: string;
    /**
     * limit
     */
    limit?: number;
    /**
     * offset
     */
    offset?: number;
    /**
     * search
     */
    search?: string;
    /**
     * status
     */
    status?: 'IN_STOCK' | 'LOW_STOCK' | 'OUT_OF_STOCK';
  }): CancelablePromise<Page_InventoryItemStockCountAndValueResponse_> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/inventory-items/items-stock-and-value',
      query: {
        categoryId: categoryId,
        limit: limit,
        offset: offset,
        search: search,
        status: status,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * getItemsTotalValue
   * @returns number OK
   * @throws ApiError
   */
  public static getItemsTotalValueUsingGet(): CancelablePromise<number> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/inventory-items/total-value',
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * getAllItemsUnpaged
   * @returns InventoryItemProjection OK
   * @throws ApiError
   */
  public static getAllItemsUnpagedUsingGet(): CancelablePromise<Array<InventoryItemProjection>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/inventory-items/unpaged',
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * getItemById
   * @returns any OK
   * @throws ApiError
   */
  public static getItemByIdUsingGet({
    itemId,
  }: {
    /**
     * itemId
     */
    itemId: string;
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/inventory-items/{itemId}',
      path: {
        itemId: itemId,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * deleteItem
   * @returns any OK
   * @throws ApiError
   */
  public static deleteItemUsingDelete({
    itemId,
  }: {
    /**
     * itemId
     */
    itemId: string;
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/inventory-items/{itemId}',
      path: {
        itemId: itemId,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
      },
    });
  }

  /**
   * getItemIssueOutHistory
   * @returns Page_InventoryItemIssueOutHistoryResponse_ OK
   * @throws ApiError
   */
  public static getItemIssueOutHistoryUsingGet({
    itemId,
    endDate,
    limit = 10,
    offset,
    search,
    startDate,
    userType,
  }: {
    /**
     * itemId
     */
    itemId: string;
    /**
     * endDate
     */
    endDate?: string;
    /**
     * limit
     */
    limit?: number;
    /**
     * offset
     */
    offset?: number;
    /**
     * search
     */
    search?: string;
    /**
     * startDate
     */
    startDate?: string;
    /**
     * userType
     */
    userType?: 'PARENT' | 'STAFF' | 'STUDENT';
  }): CancelablePromise<Page_InventoryItemIssueOutHistoryResponse_> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/inventory-items/{itemId}/issue-out-history',
      path: {
        itemId: itemId,
      },
      query: {
        endDate: endDate,
        limit: limit,
        offset: offset,
        search: search,
        startDate: startDate,
        userType: userType,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * getItemStockUpHistory
   * @returns Page_InventoryItemStockUpHistoryResponse_ OK
   * @throws ApiError
   */
  public static getItemStockUpHistoryUsingGet({
    itemId,
    endDate,
    limit = 10,
    merchantId,
    offset,
    search,
    startDate,
  }: {
    /**
     * itemId
     */
    itemId: string;
    /**
     * endDate
     */
    endDate?: string;
    /**
     * limit
     */
    limit?: number;
    /**
     * merchantId
     */
    merchantId?: string;
    /**
     * offset
     */
    offset?: number;
    /**
     * search
     */
    search?: string;
    /**
     * startDate
     */
    startDate?: string;
  }): CancelablePromise<Page_InventoryItemStockUpHistoryResponse_> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/inventory-items/{itemId}/stock-up-history',
      path: {
        itemId: itemId,
      },
      query: {
        endDate: endDate,
        limit: limit,
        merchantId: merchantId,
        offset: offset,
        search: search,
        startDate: startDate,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }
}
